import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useMember } from "../context/MemberContext";
import { fetchToolData, davetliKey } from "../lib/toolCloud";
import { INVITATION_TEMPLATES, InvitationCard } from "./InvitationCard";
import PhotoCropDialog from "./PhotoCropDialog";
import { InvitationSmsBox } from "./InvitationSmsBox";
import { Globe, Palette, PenLine, Send, Users, Check, Loader2, Copy, MessageCircle, Link2, ThumbsUp, ThumbsDown, ImagePlus, X, RefreshCw, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const waPhone = (p) => {
  let d = (p || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("90")) return d;
  if (d.startsWith("0")) return "90" + d.slice(1);
  if (d.length === 10) return "90" + d;
  return d;
};

const readGuests = (mid) => {
  try {
    const raw = localStorage.getItem(davetliKey(mid));
    if (raw) return (JSON.parse(raw).guests || []);
  } catch {}
  return [];
};

const STEPS = [
  { id: "design", label: "Tasarım", icon: Palette },
  { id: "info", label: "Bilgiler", icon: PenLine },
  { id: "send", label: "Gönder", icon: Send },
  { id: "rsvp", label: "Yanıtlar", icon: Users },
];

const EMPTY = { template: "classic-rose", brideName: "", groomName: "", date: "", time: "", venue: "", city: "", address: "", mapUrl: "", note: "", photo: "" };

const InvitationTool = () => {
  const { token, member } = useMember();
  const mid = member?.id;
  const authH = { Authorization: `Bearer ${token}` };
  const [step, setStep] = useState("design");
  const [form, setForm] = useState(EMPTY);
  const [invId, setInvId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [guests, setGuests] = useState([]);
  const [rsvp, setRsvp] = useState({ rsvps: [], summary: { yes: 0, no: 0, guests: 0 } });
  const [pickerOpen, setPickerOpen] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [bulkIndex, setBulkIndex] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/member/invitation`, { headers: authH })
      .then((r) => { if (r.data) { setForm({ ...EMPTY, ...r.data }); setInvId(r.data.id); setPickerOpen(false); } })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const shareUrl = invId ? `${window.location.origin}/davetiye/${invId}` : "";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/member/invitation`, form, { headers: authH });
      setInvId(r.data.id);
      setStep("send");
    } catch {}
    finally { setSaving(false); }
  };

  const onPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadCropped = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "crop.jpg");
      const r = await axios.post(`${API}/member/invitation/photo`, fd, { headers: { ...authH, "Content-Type": "multipart/form-data" } });
      set("photo", r.data.url);
      setCropSrc(null);
    } catch {}
    finally { setUploading(false); }
  };

  const loadRsvps = useCallback(() => {
    axios.get(`${API}/member/invitation/rsvps`, { headers: authH })
      .then((r) => setRsvp(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (step === "send") {
      setBulkIndex(null);
      fetchToolData("davetli", token).then((d) => setGuests(Array.isArray(d?.guests) ? d.guests : readGuests(mid)));
    }
    if (step === "rsvp") loadRsvps();
  }, [step, loadRsvps, token, mid]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  };

  const msg = `${form.brideName || "Gelin"} & ${form.groomName || "Damat"} düğününe davetlisiniz! 💍\nDavetiye: ${shareUrl}`;
  const withPhone = guests.filter((g) => (g.phone || "").trim());
  const waLink = (g) => `https://wa.me/${waPhone(g.phone)}?text=${encodeURIComponent(msg.replace(shareUrl, `${shareUrl}?g=${g.id}&n=${encodeURIComponent(g.name || "")}`))}`;

  const startBulk = () => {
    if (!withPhone.length) return;
    window.open(waLink(withPhone[0]), "_blank");
    setBulkIndex(1);
  };
  const nextBulk = () => {
    if (bulkIndex == null || bulkIndex >= withPhone.length) return;
    window.open(waLink(withPhone[bulkIndex]), "_blank");
    setBulkIndex((i) => i + 1);
  };

  if (loading) {
    return <div className="bg-white rounded-2xl border border-[#F0DDE0] py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#9E2A3B]" /></div>;
  }

  const selTpl = INVITATION_TEMPLATES.find((t) => t.id === form.template) || INVITATION_TEMPLATES[0];

  return (
    <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 sm:p-8" data-testid="invitation-tool">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] flex items-center justify-center">
          <Globe className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold text-[#2A2224]">Dijital Davetiyem</h1>
          <p className="text-sm text-[#7C6E70]">Tasarım seçin, bilgileri girin, davetlilere gönderin</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {STEPS.map((s) => {
          const I = s.icon;
          const on = step === s.id;
          const disabled = (s.id === "send" || s.id === "rsvp") && !invId;
          return (
            <button
              key={s.id}
              onClick={() => !disabled && setStep(s.id)}
              disabled={disabled}
              data-testid={`inv-step-${s.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${on ? "bg-[#9E2A3B] text-white" : disabled ? "bg-[#FBF5F0] text-[#c9bcbe] cursor-not-allowed" : "bg-[#FBF5F0] text-[#7C6E70] hover:bg-[#F6E4E6]"}`}
            >
              <I className="w-4 h-4" /> {s.label}
            </button>
          );
        })}
      </div>

      {/* DESIGN */}
      {step === "design" && (
        <div data-testid="inv-design">
          {pickerOpen ? (
            <>
              <p className="text-sm text-[#7C6E70] mb-4">Beğendiğiniz tasarıma tıklayın ({INVITATION_TEMPLATES.length} tasarım)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {INVITATION_TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { set("template", t.id); setPickerOpen(false); }}
                    data-testid={`inv-template-${t.id}`}
                    className="rounded-xl overflow-hidden border-2 border-[#F0DDE0] hover:border-[#C6A15B] transition-all text-left"
                  >
                    <div className="h-40 relative">
                      <InvitationCard template={t} data={{ brideName: form.brideName || "Ayşe", groomName: form.groomName || "Mehmet" }} compact />
                    </div>
                    <div className="px-2 py-1.5 bg-white text-xs font-medium text-[#5b4d4f]">{t.name}</div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div data-testid="inv-selected-design">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-[#7C6E70]">Seçilen tasarım: <span className="font-semibold text-[#2A2224]">{selTpl.name}</span></p>
                <button onClick={() => setPickerOpen(true)} data-testid="inv-change-design" className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#9E2A3B] text-[#9E2A3B] text-sm font-medium hover:bg-[#9E2A3B] hover:text-white transition-colors">
                  <RefreshCw className="w-4 h-4" /> Tasarımı Değiştir
                </button>
              </div>
              <div className="max-w-md mx-auto rounded-2xl overflow-hidden border border-[#F0DDE0] shadow-lg min-h-[420px]">
                <InvitationCard template={form.template} data={form} />
              </div>
              <div className="mt-5 flex justify-center">
                <button onClick={() => setStep("info")} data-testid="inv-goto-info" className="px-6 py-2.5 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#861f2f] transition-colors">Devam Et →</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* INFO */}
      {step === "info" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="inv-info">
          <div className="space-y-3">
            {/* Photo upload */}
            <div>
              <label className="text-sm text-[#7C6E70]">Çift Fotoğrafı (opsiyonel)</label>
              <div className="mt-1 flex items-center gap-3">
                {form.photo ? (
                  <div className="relative">
                    <img src={form.photo.startsWith("http") ? form.photo : `${process.env.REACT_APP_BACKEND_URL}${form.photo}`} alt="Çift" className="w-16 h-16 rounded-full object-cover border-2 border-[#C6A15B]" />
                    <button onClick={() => set("photo", "")} data-testid="inv-photo-remove" className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#9E2A3B] text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#FBF5F0] border border-dashed border-[#D9C4C7] flex items-center justify-center text-[#c9a0a6]"><ImagePlus className="w-6 h-6" /></div>
                )}
                <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#F0DDE0] text-sm text-[#5b4d4f] cursor-pointer hover:bg-[#FBF5F0] transition-colors" data-testid="inv-photo-label">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Fotoğraf Yükle
                  <input ref={fileRef} type="file" accept="image/*" onChange={onPhotoSelect} className="hidden" data-testid="inv-photo-input" />
                </label>
              </div>
            </div>

            {[
              ["brideName", "Gelin Adı"],
              ["groomName", "Damat Adı"],
              ["venue", "Mekan Adı"],
              ["city", "Şehir"],
              ["address", "Adres"],
              ["mapUrl", "Harita Linki (opsiyonel)"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="text-sm text-[#7C6E70]">{label}</label>
                <input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`inv-field-${k}`}
                  className="w-full mt-1 px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]" />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-[#7C6E70]">Tarih</label>
                <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="inv-field-date"
                  className="w-full mt-1 px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]" />
              </div>
              <div>
                <label className="text-sm text-[#7C6E70]">Saat</label>
                <input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="inv-field-time"
                  className="w-full mt-1 px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]" />
              </div>
            </div>
            <div>
              <label className="text-sm text-[#7C6E70]">Not / Mesaj</label>
              <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} data-testid="inv-field-note"
                placeholder="Örn: Mutluluğumuzu paylaşmaktan onur duyarız"
                className="w-full mt-1 px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B] resize-y" />
            </div>
            <button onClick={save} disabled={saving} data-testid="inv-save-btn"
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-lg transition-all disabled:opacity-70">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Kaydet ve Link Oluştur
            </button>
          </div>

          {/* Live preview */}
          <div className="lg:sticky lg:top-4 self-start">
            <p className="text-xs text-[#9c8f90] mb-2 text-center">Önizleme</p>
            <div className="rounded-2xl overflow-hidden border border-[#F0DDE0] shadow-lg min-h-[420px]">
              <InvitationCard template={form.template} data={form} />
            </div>
          </div>
        </div>
      )}

      {/* SEND */}
      {step === "send" && (
        <div data-testid="inv-send">
          <div className="rounded-xl bg-[#FBF3E4] border border-[#EBD9AF] p-4 mb-5">
            <p className="text-sm text-[#8a6d1f] mb-2 flex items-center gap-2"><Link2 className="w-4 h-4" /> Davetiye Linkiniz</p>
            <div className="flex gap-2">
              <input readOnly value={shareUrl} data-testid="inv-share-url" className="flex-1 px-3 py-2 rounded-lg border border-[#EBD9AF] bg-white text-sm text-[#5b4d4f]" />
              <button onClick={copyLink} data-testid="inv-copy-btn" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#861f2f] transition-colors">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Kopyalandı" : "Kopyala"}
              </button>
              <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="inv-preview-link" className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#9E2A3B] text-[#9E2A3B] text-sm font-medium hover:bg-[#9E2A3B] hover:text-white transition-colors">Aç</a>
            </div>
          </div>

          <div className="rounded-xl border border-[#BFE8CF] bg-[#F1FBF5] p-4 mb-5" data-testid="inv-share-all-box">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#1f6b3f]">Tek mesajla hepsine gönderin</p>
                <p className="text-xs text-[#4c7a5e] mt-1 max-w-md">WhatsApp açılınca davetlilerinizi, bir grubu ya da toplu mesaj listenizi işaretleyip tek seferde gönderin.</p>
              </div>
              <a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noopener noreferrer" data-testid="inv-share-all"
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#25D366] text-white text-sm font-semibold hover:opacity-90 transition-opacity shrink-0">
                <MessageCircle className="w-4 h-4" /> WhatsApp'ta Kişileri Seç
              </a>
            </div>
          </div>

          <InvitationSmsBox guests={withPhone} token={token} />

          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <p className="text-sm font-semibold text-[#2A2224]">Ya da tek tek gönderin</p>
            {withPhone.length > 0 && (
              bulkIndex == null ? (
                <button onClick={startBulk} data-testid="inv-bulk-start" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#25D366] text-white text-sm font-medium hover:opacity-90 transition-opacity">
                  <MessageCircle className="w-4 h-4" /> Tüm Davetlilere Gönder ({withPhone.length})
                </button>
              ) : bulkIndex < withPhone.length ? (
                <button onClick={nextBulk} data-testid="inv-bulk-next" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#25D366] text-white text-sm font-medium hover:opacity-90 transition-opacity">
                  Sonraki: {withPhone[bulkIndex].name} <ChevronRight className="w-4 h-4" /> ({bulkIndex + 1}/{withPhone.length})
                </button>
              ) : (
                <span className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E6F4EA] text-[#2f7d4f] text-sm font-medium"><Check className="w-4 h-4" /> Tümü açıldı</span>
              )
            )}
          </div>
          <p className="text-xs text-[#9c8f90] mb-4">WhatsApp'a tıklayınca uygulamanız davetiye mesajıyla açılır ve kendi numaranızdan gönderirsiniz. (Telefonlu {withPhone.length} davetli)</p>

          {withPhone.length === 0 ? (
            <div className="py-8 text-center text-sm text-[#9c8f90] bg-[#FBF5F0] rounded-xl">
              Davetli Listem'de telefon numaralı davetli yok. Önce "Davetli Listem" aracından telefon ekleyin.
            </div>
          ) : (
            <ul className="divide-y divide-[#F6E4E6]" data-testid="inv-guest-list">
              {withPhone.map((g, i) => (
                <li key={g.id} className={`flex items-center gap-3 py-2.5 ${bulkIndex != null && i < bulkIndex ? "opacity-60" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#2A2224]">{g.name}</p>
                    <p className="text-xs text-[#9c8f90]">{g.phone}</p>
                  </div>
                  {bulkIndex != null && i < bulkIndex && <Check className="w-4 h-4 text-[#2f7d4f]" />}
                  <a href={waLink(g)} target="_blank" rel="noreferrer"
                    data-testid={`inv-wa-${g.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#25D366] text-white text-xs font-medium hover:opacity-90 transition-opacity">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* RSVP */}
      {step === "rsvp" && (
        <div data-testid="inv-rsvp">
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="rounded-xl bg-[#E6F4EA] p-4 text-center">
              <ThumbsUp className="w-5 h-5 mx-auto mb-1 text-[#2f7d4f]" />
              <p className="text-2xl font-bold text-[#2f7d4f]" data-testid="rsvp-yes">{rsvp.summary.yes}</p>
              <p className="text-xs text-[#5b7d66]">Katılıyor</p>
            </div>
            <div className="rounded-xl bg-[#FBEAEA] p-4 text-center">
              <ThumbsDown className="w-5 h-5 mx-auto mb-1 text-[#C0392B]" />
              <p className="text-2xl font-bold text-[#C0392B]" data-testid="rsvp-no">{rsvp.summary.no}</p>
              <p className="text-xs text-[#a56b66]">Katılmıyor</p>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] p-4 text-center text-white">
              <Users className="w-5 h-5 mx-auto mb-1" />
              <p className="text-2xl font-bold">{rsvp.summary.guests}</p>
              <p className="text-xs text-white/80">Toplam Kişi</p>
            </div>
          </div>
          {rsvp.rsvps.length === 0 ? (
            <div className="py-8 text-center text-sm text-[#9c8f90]">Henüz yanıt gelmedi. Davetiyeyi paylaştıkça yanıtlar burada görünecek.</div>
          ) : (
            <ul className="divide-y divide-[#F6E4E6]">
              {rsvp.rsvps.map((r) => (
                <li key={r.id} className="flex items-center gap-3 py-2.5">
                  <span className={`w-2 h-2 rounded-full ${r.status === "yes" ? "bg-[#2f7d4f]" : "bg-[#C0392B]"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#2A2224]">{r.name} {r.status === "yes" && r.guests > 1 && <span className="text-xs text-[#9c8f90]">· {r.guests} kişi</span>}</p>
                    {r.message && <p className="text-xs text-[#9c8f90]">{r.message}</p>}
                  </div>
                  <span className={`text-xs font-medium ${r.status === "yes" ? "text-[#2f7d4f]" : "text-[#C0392B]"}`}>{r.status === "yes" ? "Katılıyor" : "Katılmıyor"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <PhotoCropDialog
        open={!!cropSrc}
        src={cropSrc}
        busy={uploading}
        onClose={() => setCropSrc(null)}
        onConfirm={uploadCropped}
      />
    </div>
  );
};

export default InvitationTool;
